import { z } from 'zod';

export enum TaxationMethod {
  ACCRUAL = 'ACCRUAL', // Soll-Versteuerung (Standard)
  CASH = 'CASH',       // Ist-Versteuerung (Tax due on payment)
  FLAT_RATE = 'FLAT_RATE', // Pauschalierung (e.g. Agriculture)
  MARGIN = 'MARGIN',   // Differenzbesteuerung (Resellers)
  EXEMPT = 'EXEMPT',   // Steuerbefreit (General Exemption)
}

export enum TaxPeriod {
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
  YEARLY = 'YEARLY',
}

export enum AccountingSystem {
  DATEV = 'DATEV',
  LEXOFFICE = 'LEXOFFICE',
  SEVDESK = 'SEVDESK',
  SAGE = 'SAGE',
  ADDISON = 'ADDISON',
  QUICKBOOKS = 'QUICKBOOKS',
  XERO = 'XERO',
  OTHER = 'OTHER',
}

export const TaxConfigSchema = z.object({
  // Basic Jurisdiction & Identity
  countryCode: z.string().length(2).default('DE'), // e.g. DE
  standardTaxRate: z.number().default(19), // e.g. 19 for DE
  vatId: z.string().trim().optional(), // UST-ID
  taxNumber: z.string().trim().optional(), // Steuernummer
  taxOffice: z.string().trim().optional(), // Finanzamt
  creditorIdentifier: z.string().trim().optional(), // SEPA Gläubiger-ID

  // Taxation Logic
  taxationMethod: z.nativeEnum(TaxationMethod).default(TaxationMethod.ACCRUAL),
  taxPeriod: z.nativeEnum(TaxPeriod).default(TaxPeriod.MONTHLY),
  hasPermanentExtension: z.boolean().default(false), // Dauerfristverlängerung

  // Regulation Status
  isSmallBusiness: z.boolean().default(false), // Kleinunternehmerregelung
  isOssRegistered: z.boolean().default(false), // Registered for OSS
  isIossRegistered: z.boolean().default(false), // Registered for IOSS

  // Legal Texts / Compliance Notes
  smallBusinessNote: z.string().trim().optional().default('Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.'),
  reverseChargeNote: z.string().trim().optional().default('Steuerschuldnerschaft des Leistungsempfängers (Reverse Charge).'),
  intraCommunitySupplyNote: z.string().trim().optional().default('Steuerfreie innergemeinschaftliche Lieferung.'),
  exportDeliveryNote: z.string().trim().optional().default('Steuerfreie Ausfuhrlieferung.'),
  invoiceFooterText: z.string().trim().optional(), // General legal footer

  // Payment Terms & Discounts (Compliance relevant for Tax Point)
  defaultPaymentTermsDays: z.number().int().min(0).default(14),
  cashDiscountDays: z.number().int().min(0).default(0),
  cashDiscountPercentage: z.number().min(0).max(100).default(0),

  // Compliance & Retention
  retentionPeriodYears: z.number().int().min(1).max(30).default(10), // GoBD usually 10 years
  accountingSystem: z.nativeEnum(AccountingSystem).default(AccountingSystem.OTHER),
});

export type TaxConfig = z.infer<typeof TaxConfigSchema>;
